export const RANK_TIERS = [
  { min: 0, name: 'Начинающий' },
  { min: 550, name: 'Новичок' },
  { min: 650, name: 'Ученик' },
  { min: 750, name: 'Подмастерье' },
  { min: 850, name: 'Игрок' },
  { min: 950, name: 'Практикант' },
  { min: 1000, name: 'Любитель' },
  { min: 1100, name: 'Опытный' },
  { min: 1200, name: 'Кандидат' },
  { min: 1300, name: 'Мастер' },
  { min: 1400, name: 'Эксперт' },
  { min: 1500, name: 'Гроссмейстер' },
  { min: 1600, name: 'Легенда' },
  { min: 1700, name: 'Чемпион' },
] as const;

export type Rank = (typeof RANK_TIERS)[number]['name'];

const TOP_RANK_MIN = RANK_TIERS[RANK_TIERS.length - 1]!.min;
const TOP_RANK_PROGRESS_MAX = 400;

export const AFK_WARN_MS = 15_000;
export const AFK_FORFEIT_MS = 30_000;
export const AFK_TOTAL_MS = AFK_WARN_MS + AFK_FORFEIT_MS;

export type GameMode = 'ai' | 'online' | 'friend' | 'local';
export type Theme = 'light' | 'dark' | 'auto';
export type Language = 'ru' | 'en';

export interface UserSettings {
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  moveHints: boolean;
  theme: Theme;
  language: Language;
  notificationsEnabled: boolean;
}

export interface User {
  id: string;
  publicId: string;
  nickname: string;
  email: string | null;
  rating: number;
  peakRating: number;
  rank: Rank;
  avatarUrl: string | null;
  online: boolean;
  aiRating: number;
  aiWinStreak: number;
  aiLossStreak: number;
  settings: UserSettings;
}

export interface MatchSummary {
  moves: number;
  timeSec: number;
  wallsUsedP1: number;
  wallsUsedP2: number;
  startWalls: number;
}

export interface AwardDef {
  key: string;
  title: string;
  description: string;
  glyph: string;
  target: number;
}

export const DEFAULT_SETTINGS: UserSettings = {
  soundEnabled: true,
  vibrationEnabled: true,
  moveHints: true,
  theme: 'light',
  language: 'ru',
  notificationsEnabled: true,
};

export function rankFromRating(rating: number): Rank {
  let current: Rank = RANK_TIERS[0]!.name;
  for (const tier of RANK_TIERS) {
    if (rating >= tier.min) current = tier.name;
  }
  return current;
}

/** 0-based index of the current rank tier (for UI styling). */
export function rankTierIndex(rating: number): number {
  let index = 0;
  for (let i = 0; i < RANK_TIERS.length; i++) {
    if (rating >= RANK_TIERS[i]!.min) index = i;
  }
  return index;
}

/** Progress toward the next rank tier for UI progress bars. */
export function rankProgress(rating: number): { value: number; max: number; label: string } {
  const index = rankTierIndex(rating);
  const current = RANK_TIERS[index]!;
  if (index >= RANK_TIERS.length - 1) {
    return {
      value: Math.min(TOP_RANK_PROGRESS_MAX, rating - TOP_RANK_MIN),
      max: TOP_RANK_PROGRESS_MAX,
      label: 'Максимальный ранг',
    };
  }
  const next = RANK_TIERS[index + 1]!;
  return {
    value: rating - current.min,
    max: next.min - current.min,
    label: `До ранга «${next.name}»`,
  };
}

export function eloDelta(rating: number, opponentRating: number, won: boolean, gamesPlayed: number): number {
  const K = gamesPlayed < 30 ? 24 : 16;
  const E = 1 / (1 + Math.pow(10, (opponentRating - rating) / 400));
  const S = won ? 1 : 0;
  return Math.round(K * (S - E));
}

export function generatePublicId(): string {
  const hex = Math.random().toString(16).slice(2, 8).toUpperCase();
  return `KDR-${hex}`;
}

export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[bytes[i]! % chars.length];
    if (i === 2) code += '-';
  }
  return code;
}

/** Strip separators and keep up to 6 alphanumeric chars (uppercase). */
export function roomCodeChars(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
}

/** Format live input as XXX or XXX-YYY. */
export function formatRoomCodeInput(raw: string): string {
  const chars = roomCodeChars(raw);
  if (chars.length <= 3) return chars;
  return `${chars.slice(0, 3)}-${chars.slice(3)}`;
}

/** Canonical room code `XXX-YYY`, or empty if incomplete. */
export function normalizeRoomCode(raw: string): string {
  const chars = roomCodeChars(raw);
  if (chars.length !== 6) return chars.length ? formatRoomCodeInput(chars) : '';
  return `${chars.slice(0, 3)}-${chars.slice(3)}`;
}

export const AWARDS_CATALOG: AwardDef[] = [
  { key: 'first_game', title: 'Дебютант', description: 'Сыграна первая партия', glyph: '◉', target: 1 },
  { key: 'first_win', title: 'Первая победа', description: 'Выиграна первая партия', glyph: '◈', target: 1 },
  { key: 'speed_win', title: 'Скороход', description: 'Победа менее чем за 20 ходов', glyph: '▲', target: 1 },
  { key: 'clean_win', title: 'Чистая игра', description: 'Победа без стенок', glyph: '◇', target: 1 },
  { key: 'first_friend', title: 'Первый друг', description: 'Добавлен первый друг', glyph: '◎', target: 1 },
  { key: 'all_walls', title: 'Полководец', description: 'Использованы все стены в партии', glyph: '▦', target: 1 },
  { key: 'streak_5', title: 'Серия 5', description: '5 побед подряд', glyph: '◆', target: 5 },
  { key: 'wall_master', title: 'Стратег', description: '10 побед со стенками', glyph: '▣', target: 10 },
  { key: 'marathon_100', title: 'Марафон', description: '100 сыгранных партий', glyph: '∞', target: 100 },
  { key: 'climb_1200', title: 'Восхождение', description: 'Рейтинг 1200', glyph: '↑', target: 1200 },
  { key: 'blitz_10', title: 'Блиц-мастер', description: '10 побед на 7×7', glyph: '⚡', target: 10 },
  { key: 'legend_50', title: 'Легенда коридора', description: '50 побед онлайн', glyph: '★', target: 50 },
];
